import type { ChangeEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input, Switch } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import { hasRewriteValue } from '../../../integrationRewriteModel';
import type { IntegrationFormValue } from '../../../integrationFormSchema';

export function IntegrationRewriteRow({ fieldName, index }: { fieldName: string; index: number }) {
  const form = useFormContext<IntegrationFormValue>();
  const prefix = `integrationRewriteSets.${String(index)}` as `integrationRewriteSets.${number}`;
  const inputId = `integration-rewrite-${String(index)}`;
  const value = form.watch(`${prefix}.value`);
  const hasValue = hasRewriteValue(value);
  const valueRegistration = form.register(`${prefix}.value`);

  return (
    <div className="border-input grid min-w-0 gap-3 border-t px-4 py-3 first:border-t-0 md:grid-cols-[minmax(150px,0.8fr)_minmax(0,1.5fr)_90px_90px] md:items-center">
      <label className="text-sm font-medium" htmlFor={inputId}>
        {rewritePropertyLabel(fieldName)}
      </label>
      <Input
        id={inputId}
        className="font-mono text-sm"
        {...valueRegistration}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          void valueRegistration.onChange(event);
          const nextHasValue = hasRewriteValue(event.currentTarget.value);
          if (nextHasValue) {
            form.setValue(`${prefix}.nullify`, false, { shouldDirty: true });
          } else {
            form.setValue(`${prefix}.overwrite`, false, { shouldDirty: true });
          }
        }}
      />
      <label className="flex items-center justify-between gap-3 text-sm md:justify-center">
        <span className="md:sr-only">{dashboardT('integrations.rewrite.rewrite')}</span>
        <Controller
          control={form.control}
          name={`${prefix}.overwrite`}
          render={({ field }) => (
            <Switch
              checked={field.value}
              disabled={!hasValue}
              aria-label={dashboardT('integrations.rewrite.rewriteProperty', {
                property: rewritePropertyLabel(fieldName),
              })}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </label>
      <label className="flex items-center justify-between gap-3 text-sm md:justify-center">
        <span className="md:sr-only">{dashboardT('integrations.rewrite.nullify')}</span>
        <Controller
          control={form.control}
          name={`${prefix}.nullify`}
          render={({ field }) => (
            <Switch
              checked={field.value}
              disabled={hasValue}
              aria-label={dashboardT('integrations.rewrite.nullifyProperty', {
                property: rewritePropertyLabel(fieldName),
              })}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </label>
    </div>
  );
}

function rewritePropertyLabel(field: string) {
  const property = field.split('.').at(-1) ?? field;
  const words = property
    .replaceAll('_', ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : field;
}
